import { useState, useEffect, useCallback } from 'react'
import { db } from '../lib/supabase'

export function useTransactions(businessId, options = {}) {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTransactions = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    const { data, error } = await db.getTransactions(businessId, options)
    if (error) {
      setError(error)
    } else {
      setTransactions(data)
    }
    setLoading(false)
  }, [businessId, options.limit])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  const createTransaction = async (transaction) => {
    const { data, error } = await db.createTransaction({
      ...transaction,
      business_id: businessId,
    })
    if (!error && data) {
      setTransactions(prev => [data, ...prev])
    }
    return { data, error }
  }

  const updateTransaction = async (id, updates) => {
    const { data, error } = await db.updateTransaction(id, updates)
    if (!error && data) {
      setTransactions(prev => prev.map(t => t.id === id ? data : t))
    }
    return { data, error }
  }

  const deleteTransaction = async (id) => {
    const { error } = await db.deleteTransaction(id)
    if (!error) {
      setTransactions(prev => prev.filter(t => t.id !== id))
    }
    return { error }
  }

  // Calculate financials
  const financials = {
    revenue: transactions
      .filter(t => t.type === 'revenue')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    expenses: transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
  }
  financials.profit = financials.revenue - financials.expenses

  return {
    transactions,
    financials,
    loading,
    error,
    refetch: fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  }
}

export function useAllTransactions(options = {}) {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTransactions = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getAllTransactions(options)
    if (error) {
      setError(error)
    } else {
      setTransactions(data)
    }
    setLoading(false)
  }, [options.limit])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  return {
    transactions,
    loading,
    error,
    refetch: fetchTransactions,
  }
}

export default useTransactions
